import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Collapsible from "react-collapsible";
import { useWatchStateChange } from "../../../util/watchStateChange";
import FormLabel from "./FormLabel";

const dropDownStyle = {
	float: "right",
	marginRight: "5px",
	transition: "1s",
};

export const StandardCol = ({ onInputChange, selectedPreset, clearPreset, currentFieldSelection, selectedDataType, content, setCollapsed, loading, isEditPreset, ...props }) => {

	const preset = useMemo(() => selectedPreset?.config?.dataType === selectedDataType && selectedPreset?.config?.fields?.STANDARD, [selectedDataType, selectedPreset]);
	const fieldSelection = currentFieldSelection?.[selectedDataType]?.['STANDARD'];
	
	const [checkBoxReady, setCheckBoxReady] = useState(false);
	const [defaultKeys, setDefaultKeys] = useState([]);
	const [readOnlyKeys, setReadOnlyKeys] = useState([]);

	const checkboxValues = fieldSelection || preset || defaultKeys;
	const selecetAllValue = checkboxValues.length >= content.length;

	const [col0, setCol0] = useState([]);
	const [col1, setCol1] = useState([]);
	const [col2, setCol2] = useState([]);

	/**
	 * Dividing lists of standard info into 3 columns
	 * @param {Array} info content
	 */
	const processStandard = useCallback(content => {
		const length = Math.floor(content.length/3);
		const col0Stop = length + (content.length % 3 > 0 ? 1 : 0);
		const col1Stop = col0Stop + length + (content.length % 3 > 1 ? 1 : 0);
		const col2Stop = col1Stop + length + (content.length % 3 > 2 ? 1 : 0);
		setCol0(content.slice(0, col0Stop));
		setCol1(content.slice(col0Stop, col1Stop));
		setCol2(content.slice(col1Stop, col2Stop));
		setCheckBoxReady(true);
		loading();
	}, [ loading ]);

	/**
	 * A function render checkbox of key
	 * @param {array} cols all keys of a column
	 * @returns an array of checkbox of one column
	 */
	const checkedBoxRendered = (cols) => {
		if (!cols?.length) { return }

		const item = cols.map(col => {
			if(!col.readOnly) {
				return (
					<Row key={col.value}>
						<input
							type="checkbox"
							name={col.value}
							onChange={handleOnChange}
							checked={checkboxValues.includes(col.value)}
						/>
						{col.label}
					</Row>
				);
			}
			return (
				<Row key={col.value}>
					<input
						type="checkbox"
						name={col.value}
						checked={true}
						readOnly
					/>
					{col.label}
				</Row>
			);
		});

		return item;
	};

	// if user is not editing the preset, clear preset
	const clearPresetIfNotEdit = useCallback(() => {
		if(!isEditPreset) {
			clearPreset();
		}
	}, [ isEditPreset, clearPreset ]);

	const handleSetParentStates = useCallback(newCK => {
		const inputChange = [{ section: 'STANDARD', data: newCK }];
		if(selectedPreset?.config?.dataType === selectedDataType) {
			if(selectedPreset?.config?.fields?.ADVANCED && !currentFieldSelection[selectedDataType]?.ADVANCED) {
				inputChange.push({ section: 'ADVANCED', data: selectedPreset.config.fields.ADVANCED });
			}
			if(selectedPreset?.config?.fields?.DATE_RANGE && !currentFieldSelection[selectedDataType]?.DATE_RANGE) {
				inputChange.push({ section: 'DATE_RANGE', data: selectedPreset.config.fields.DATE_RANGE });
			}
		}
		onInputChange(inputChange);
		setTimeout(() => clearPresetIfNotEdit());
	}, [clearPresetIfNotEdit, currentFieldSelection, onInputChange, selectedDataType, selectedPreset]);

	const handleSelectAll = useCallback((e) => {
		const newCK = !e.target.checked ? readOnlyKeys : content.map(v => v.value);
		handleSetParentStates(newCK);
	}, [preset, readOnlyKeys, content, handleSetParentStates])

	/**
	 * A function to handle changing of value of an input tag
	 * This is checkbox input so it would record all selected boxes
	 * @param {event} e event of onChange of input tag
	 */
	function handleOnChange(e) {
		let { name, checked } = e.target;
		let newCK = [...checkboxValues, name];
		if (!checked) {
			newCK = checkboxValues.filter((item) => item !== name);
		}
		handleSetParentStates(newCK);
	}

	/**
	 * State form state
	 */
	useWatchStateChange((depIndex, prevValue, currentValue) => {
		if(depIndex !== 0) { return } // focus on content dependency
		if (!content?.length) { return }
		const readOnly = content.filter(({ readOnly }) => readOnly === 1);
		const defaultCheckedKey = readOnly.map(({ value }) => value);
		processStandard(content);
		setReadOnlyKeys(readOnly.map(({ value }) => value));
		setDefaultKeys(defaultCheckedKey);
		setCollapsed(false);
	}, [content, processStandard, setCollapsed]);

	return (
		checkBoxReady && (
			<>
				<Collapsible
					trigger={
						<div className="list-group-item">
							<FormLabel popoverHelp={<div>You can check any checkboxes to see data you want execpt some read-only checkboxes like "Date", "Year", etc.</div>}>Standard Information Column Collection</FormLabel>
							<span
								className={
									!props.isCollapsed
										? "glyphicon glyphicon-menu-down"
										: "glyphicon glyphicon-menu-up"
								}
								aria-hidden="true"
								style={dropDownStyle}
							></span>
						</div>
					}
					transitionTime="200"
					easing="ease-in"
					onTriggerClosing={() => setCollapsed(true)}
					open={!props.isCollapsed}
					onOpening={() => setCollapsed(false)}
				>
					<Container fluid style={{ padding: '1em', border: '1px solid #ddd' }}>
						<Row>
							<Col sm={6}>
								<input
									type="checkbox"
									name="all"
									onChange={handleSelectAll}
									checked={selecetAllValue}
								/>
								Select all
							</Col>
						</Row>
						<Row style={{ margin: "auto 5%" }}>
							<Col sm={4}>{checkedBoxRendered(col0)}</Col>
							<Col sm={4}>{checkedBoxRendered(col1)}</Col>
							<Col sm={4}>{checkedBoxRendered(col2)}</Col>
						</Row>
					</Container>
				</Collapsible>
			</>
		)
	);
};
